import { useUser } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { authTokenManager } from '@/utils/authTokenManager';
import { useUserInit } from '@/contexts/UserInitContext';

/**
 * Hook to fetch and manage user notifications
 * Includes automatic polling to check for new notifications
 */
export function useUserNotifications() {
  const { isLoaded, isSignedIn } = useUser();
  const { isInitialized } = useUserInit();
  const queryClient = useQueryClient();

  // Fetch notifications with polling every 60 seconds
  // Include auth readiness check to prevent 401 errors during session expiration
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['user-notifications'],
    queryFn: () => apiService.getUserNotifications(),
    enabled: isLoaded && isSignedIn && isInitialized && authTokenManager.isReady(),
    staleTime: 1000 * 50, // Cache for 50 seconds
    refetchInterval: (query) => {
      // Stop polling if we've had auth errors (401/403)
      // This prevents repeated errors when session expires during background polling
      if (query.state.error && (query.state.error as any)?.response?.status === 401) {
        return false; // Stop polling
      }
      return 1000 * 60; // Poll every 60 seconds normally
    },
    retry: (failureCount, error) => {
      // Don't retry auth errors - let the user re-authenticate
      if ((error as any)?.response?.status === 401 || (error as any)?.response?.status === 403) {
        return false;
      }
      return failureCount < 1; // Retry other errors once
    }
  });

  // Mutation for marking a notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => apiService.markNotificationRead(notificationId),
    onSuccess: () => {
      // Invalidate the notifications query to refetch
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
    }
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => apiService.markAllNotificationsRead(),
    onSuccess: () => {
      // Invalidate the notifications query to refetch
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
    }
  });

  const notifications = data?.data?.notifications || [];
  const unreadCount = notifications.filter((n: any) => !n.is_read).length;

  console.log('[useUserNotifications] Debug:', {
    rawData: data,
    notifications,
    unreadCount,
    isLoading,
    error
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: (notificationId: string) => markAsReadMutation.mutate(notificationId),
    markAllAsRead: () => markAllAsReadMutation.mutate(),
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending
  };
}
